/* eslint-disable react/no-array-index-key */
import { Box, Typography, useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { ElementType, FC } from 'react';
import ErrorCircleIcon from 'src/icons/ErrorCircle';
import ExclamationCircleIcon from 'src/icons/ExclamationCircle';
import InformationCircleIcon from 'src/icons/InformationCircle';

interface NotificationItem {
  notification_user_id: number;
  notification_id: number;
  notification: {
    notification_id: number;
    notification_type: string;
    title_en: string;
    title_dk: string;
    title_fr: string;
    title_de: string;
    title_nl: string;
    description_en: string;
    description_dk: string;
    description_fr: string;
    description_de: string;
    description_nl: string;
  };
}

interface NavbarNotificationBarProps {
  notifications: NotificationItem[];
}

type NotificationType = 'error' | 'warning' | 'information';

interface NotificationConfigType {
  type: NotificationType;
  icon: ElementType;
  bgColor: string;
  textColor: string;
  borderColor: string;
  priority: number;
}

const notificationConfigMap: Record<NotificationType, NotificationConfigType> = {
  error: {
    type: 'error',
    icon: ErrorCircleIcon,
    bgColor: 'rgba(255, 221, 221, 1)',
    textColor: 'rgba(224, 73, 72, 1)',
    borderColor: 'rgba(224, 73, 72, 0.4)',
    priority: 1
  },
  warning: {
    type: 'warning',
    icon: ExclamationCircleIcon,
    bgColor: 'rgba(253, 237, 207, 1)',
    textColor: 'rgba(215, 142, 30, 1)',
    borderColor: 'rgba(215, 142, 30, 0.4)',
    priority: 2
  },
  information: {
    type: 'information',
    icon: InformationCircleIcon,
    bgColor: 'rgba(232, 245, 233, 1)',
    textColor: 'rgba(0, 139, 59, 1)',
    borderColor: 'rgba(0, 139, 59, 0.4)',
    priority: 3
  }
};

const getLocale = (): string => {
  const locale = localStorage.getItem('locale') || 'en';
  return locale;
};

const getLocalizedText = (notification: NotificationItem['notification'], field: 'title' | 'description'): string => {
  const locale = getLocale();
  const key = `${field}_${locale}` as keyof NotificationItem['notification'];
  return (notification[key] as string) || (notification[`${field}_en` as keyof NotificationItem['notification']] as string) || '';
};

const NavbarNotificationBar: FC<NavbarNotificationBarProps> = ({ notifications }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (!notifications || notifications.length === 0) {
    return null;
  }

  const sortedNotifications = [...notifications].sort((a, b) => {
    const typeA = a.notification.notification_type as NotificationType;
    const typeB = b.notification.notification_type as NotificationType;
    const priorityA = notificationConfigMap[typeA]?.priority || 99;
    const priorityB = notificationConfigMap[typeB]?.priority || 99;
    return priorityA - priorityB;
  });

  const rows: NotificationItem[][] = [];
  for (let i = 0; i < sortedNotifications.length; i += 3) {
    rows.push(sortedNotifications.slice(i, i + 3));
  }

  const renderNotificationItem = (item: NotificationItem, flexBasis: string) => {
    const notificationType = item.notification.notification_type as NotificationType;
    const config = notificationConfigMap[notificationType];

    if (!config) return null;

    const Icon = config.icon;
    const title = getLocalizedText(item.notification, 'title');
    const description = getLocalizedText(item.notification, 'description');

    return (
      <Box
        key={item.notification_user_id}
        sx={{
          display: 'flex',
          margin: '2px',
          alignItems: 'flex-start',
          flexBasis: isMobile ? '100%' : flexBasis,
          flexGrow: 1,
          maxWidth: isMobile ? '100%' : flexBasis,
          backgroundColor: config.bgColor,
          border: `1px solid ${config.borderColor}`,
          padding: '12px 16px',
          gap: '12px',
          cursor: 'normal',
          transition: 'opacity 0.2s',
          '&:hover': {
            opacity: 0.85
          },
          '& svg': {
            width: 24,
            height: 24,
            flexShrink: 0,
            marginTop: isMobile ? '2px' : 0
          },
          '& svg path': {
            fill: config.textColor
          }
        }}
      >
        <Icon />
        <Box sx={{ overflow: 'hidden', flex: 1 }}>
          <Typography
            variant="subtitle2"
            sx={{
              color: config.textColor,
              fontWeight: 600,
              ...(isMobile
                ? {
                  whiteSpace: 'normal',
                  overflow: 'visible',
                  textOverflow: 'unset',
                  wordBreak: 'break-word'
                }
                : {
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                })
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: config.textColor,
              opacity: 0.8,
              display: 'block',
              ...(isMobile
                ? {
                  whiteSpace: 'normal',
                  overflow: 'visible',
                  textOverflow: 'unset',
                  wordBreak: 'break-word'
                }
                : {
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                })
            }}
          >
            {description}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        '@media (max-width: 404px)': {
          marginTop: '2px'
        }
      }}
    >
      {rows.map((row, rowIndex) => {
        const itemCount = row.length;
        const flexBasis = `${100 / itemCount}%`;

        return (
          <Box
            key={rowIndex}
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              width: '100%',
              minHeight: isMobile ? 'auto' : '48px',
              justifyContent: 'center'
            }}
          >
            {row.map((item) => renderNotificationItem(item, flexBasis))}
          </Box>
        );
      })}
    </Box>
  );
};

export default NavbarNotificationBar;
